import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {uniq} from 'underscore';

import {ParametersForDocumentMethod_procedureStepExecutionInstances_ProcedureStepExecutionInstanceNode as PSEINode} from 'notebook/integrations/procedures/add-parameters-to-table/__generated/ParametersForDocumentMethod';
import {
  DataParameterInfo,
  EquipmentParameterInfo,
  MaterialInputParameterInfo,
  MaterialOutputParameterInfo,
} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {getFormattedDataParameterValue} from 'notebook/integrations/procedures/ProceduresUtil';
import getMaxChipsToDisplay from 'notebook/tables/export/getMaxChipsToDisplay';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {ParameterTypedValue, ParameterUsages} from 'procedures/ProcedureDomainTypes';
import {CONFIRMATION_METHOD_TO_USER_FACING_TEXT} from 'procedures/tables/ProcedureTableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import CheckableSortableTable, {
  // eslint-disable-next-line import-access/jsdoc -- This file creates a wrapper around CheckableSortableTable so it needs the props
  CheckableSortableTableProps,
  CheckableSortableTableRow,
} from 'ui-library/CheckableSortableTable';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {GRAY, GRAY_LIGHTER} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import PlainSearchInput from 'ui-library/PlainSearchInput';
import SortableTable from 'ui-library/SortableTable';

type ConditionReplicateNode = PSEINode['methodExecutionInstance']['procedureRunConditionReplicate'];
export type ConditionReplicateWithPMEI = ConditionReplicateNode & {
  procedureMethodExecutionInstanceId: string;
};

interface ParameterSelectionModalFormControlsProps {
  parameterUsages?: ParameterUsages; // Only used in procedure config
  selectedParameterIds: Array<string>;
  onChangeSelectedParameterIds: (parameterIds: Array<string>) => void;
  shouldShowValueColumn: boolean;
}

function SearchableCheckableSortableTable(
  props: Pick<CheckableSortableTableProps, 'columns' | 'onChangeChecks'> & {
    rows: Array<CheckableSortableTableRow & {name: string}>;
    objectName: string;
    numSelected: number;
  }
): JSX.Element {
  const {rows, objectName, numSelected, onChangeChecks} = props;
  const [searchText, setSearchText] = useState('');

  const filteredRows = rows.filter((row) => row.name.toLowerCase().includes(searchText.toLowerCase().trim()));

  function onSearchableChangeChecks(newCheckedRowIds: Array<string | number>): void {
    const selectedRowIds = rows.filter(({checked}) => checked).map(({id}) => id);
    const hiddenCheckedRowIds = selectedRowIds.filter((id) => !filteredRows.map(({id}) => id).includes(id));
    // We want to still keep the selection state of the rows that are not visible in the table
    const combinedCheckedRowIds = uniq([...hiddenCheckedRowIds, ...newCheckedRowIds]);
    onChangeChecks?.(combinedCheckedRowIds);
  }

  return (
    <div>
      <FieldLabel label="Search" />
      <PlainSearchInput
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
        placeholder={`Search by ${objectName} name`}
      />
      <div className={css(styles.tableWithCountWrapper)}>
        <div className={css(styles.selectedCount)}>{numSelected} selected</div>
        <CheckableSortableTable
          {...props}
          onChangeChecks={onSearchableChangeChecks}
          rows={filteredRows}
          clickable={false}
          selectable={false}
          hoverable={true}
          coloredHeader={false}
          wrapCell={true}
          className={css(styles.table)}
        />
      </div>
    </div>
  );
}

export interface ConditionReplicateSelectionModalFormControlsProps {
  conditionReplicates: Array<ConditionReplicateWithPMEI>;
  selectedConditionReplicateIds: Array<string>;
  onChangeSelectedConditionReplicateIds: (ids: Array<string>) => void;
}

export function ConditionReplicateSelectionModalFormControls(
  props: ConditionReplicateSelectionModalFormControlsProps
): JSX.Element {
  const {conditionReplicates, selectedConditionReplicateIds} = props;

  const columns = [
    SortableTable.defaultColumn('Condition', 'name'),
    SortableTable.defaultColumn('Replicate', 'replicateNumber'),
  ];

  const rows = conditionReplicates.map(({id, replicateNumber, procedureRunCondition: {name}}) => ({
    id,
    name,
    replicateNumber,
    checked: selectedConditionReplicateIds.includes(id),
  }));

  return (
    <SearchableCheckableSortableTable
      rows={rows}
      columns={columns}
      onChangeChecks={props.onChangeSelectedConditionReplicateIds}
      objectName="condition"
      numSelected={selectedConditionReplicateIds.length}
    />
  );
}
export interface DataParameterSelectionModalFormControlsProps extends ParameterSelectionModalFormControlsProps {
  allParameters: Array<DataParameterInfo>;
  showConfirmationMethod: boolean;
  filtersOutPlateParameter: boolean;
}

export function DataParameterSelectionModalFormControls(
  props: DataParameterSelectionModalFormControlsProps
): JSX.Element {
  const {
    allParameters,
    selectedParameterIds,
    showConfirmationMethod,
    filtersOutPlateParameter,
    shouldShowValueColumn,
  } = props;

  const columns = [SortableTable.defaultColumn('Parameter', 'name')];
  if (shouldShowValueColumn) {
    columns.push(SortableTable.defaultColumn('Setpoint', 'value'));
  }
  if (showConfirmationMethod) {
    columns.push(SortableTable.defaultColumn('Confirmation Method', 'confirmationMethod'));
  }

  const rows = allParameters.map((parameter) => ({
    name: parameter.name,
    id: parameter.paramId,
    value: getFormattedDataParameterValue(parameter),
    confirmationMethod: CONFIRMATION_METHOD_TO_USER_FACING_TEXT[parameter.confirmationMethod],
    checked: selectedParameterIds.includes(parameter.paramId),
  }));

  const textForNoParameter = `No ${
    filtersOutPlateParameter ? 'non-plate ' : ''
  }parameters in unit operation.`;
  return allParameters.length === 0 ? (
    <p>{textForNoParameter}</p>
  ) : (
    <SearchableCheckableSortableTable
      rows={rows}
      columns={columns}
      onChangeChecks={props.onChangeSelectedParameterIds}
      objectName="parameter"
      numSelected={selectedParameterIds.length}
    />
  );
}

export interface MaterialInputParameterSelectionModalFormControlsProps extends ParameterSelectionModalFormControlsProps {
  allParameters: Array<MaterialInputParameterInfo>;
  showConfirmationMethod: boolean;
  filtersOutPlateParameter: boolean;
}

export function MaterialInputParameterSelectionModalFormControls(
  props: MaterialInputParameterSelectionModalFormControlsProps
): JSX.Element {
  const {
    allParameters,
    selectedParameterIds,
    showConfirmationMethod,
    filtersOutPlateParameter,
    shouldShowValueColumn,
  } = props;

  const columns = [SortableTable.defaultColumn('Material Input', 'name')];

  if (shouldShowValueColumn) {
    columns.push(SortableTable.defaultColumn('Planned Material', 'material'));
    columns.push(SortableTable.defaultColumn('Planned Amount', 'amount'));
  }
  if (showConfirmationMethod) {
    columns.push(SortableTable.defaultColumn('Confirmation Method', 'confirmationMethod'));
  }

  const rows = allParameters.map((parameter) => ({
    id: parameter.paramId,
    name: parameter.name,
    material: formattedCellValueFromColumnConstraint(parameter.plannedMaterial),
    amount: parameter.plannedAmount,
    confirmationMethod: CONFIRMATION_METHOD_TO_USER_FACING_TEXT[parameter.confirmationMethod],
    checked: selectedParameterIds.includes(parameter.paramId),
  }));

  const textForNoParameter = `No ${
    filtersOutPlateParameter ? 'non-plate ' : ''
  }material inputs in unit operation.`;
  return allParameters.length === 0 ? (
    <p>{textForNoParameter}</p>
  ) : (
    <SearchableCheckableSortableTable
      rows={rows}
      columns={columns}
      onChangeChecks={props.onChangeSelectedParameterIds}
      objectName="material input"
      numSelected={selectedParameterIds.length}
    />
  );
}

export interface MaterialOutputParameterSelectionModalFormControlsProps extends ParameterSelectionModalFormControlsProps {
  allParameters: Array<MaterialOutputParameterInfo>;
}

export function MaterialOutputParameterSelectionModalFormControls(
  props: MaterialOutputParameterSelectionModalFormControlsProps
): JSX.Element {
  const {allParameters, selectedParameterIds, parameterUsages} = props;
  const usedParameterIds = new Set(Object.keys(parameterUsages ?? {}));
  const columns = [SortableTable.defaultColumn('Material Output', 'name')];
  const rows = allParameters.map((parameter) => ({
    id: parameter.paramId,
    name: parameter.name,
    checked: selectedParameterIds.includes(parameter.paramId),
    disabled: usedParameterIds.has(parameter.versionId),
  }));
  return allParameters.length === 0 ? (
    <p>No material outputs in unit operation.</p>
  ) : (
    <SearchableCheckableSortableTable
      rows={rows}
      columns={columns}
      onChangeChecks={props.onChangeSelectedParameterIds}
      objectName="material output"
      numSelected={selectedParameterIds.length}
    />
  );
}

export interface EquipmentParameterSelectionModalFormControlsProps extends ParameterSelectionModalFormControlsProps {
  allParameters: Array<EquipmentParameterInfo>;
}

/** Formats the value of a parameter info to be displayed in the table.
 * If the value is a bioentity link typed value, we return a BioentityChip to render on the table. Else, we format the value as a string.
 */
function formattedCellValueFromColumnConstraint(
  value: ParameterTypedValue | string | null
): string | JSX.Element {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value.type === FieldType.BIOENTITY_LINK) {
    const bioentityIds = value.targetFiles.map((file) => file.id) ?? [];
    return (
      <ChipList itemIds={bioentityIds} maxChips={getMaxChipsToDisplay(2, false)} chipSize={ChipSize.MINI} />
    );
  }
  // handles the other typed values that ParameterTypedValue can be
  return formatTypedValue(value);
}

export function EquipmentParameterSelectionModalFormControls(
  props: EquipmentParameterSelectionModalFormControlsProps
): JSX.Element {
  const {allParameters, selectedParameterIds, shouldShowValueColumn} = props;
  const columns = [
    SortableTable.defaultColumn('Equipment', 'name'),
    SortableTable.defaultColumn('Model(s)', 'models'),
  ];
  if (shouldShowValueColumn) {
    columns.push(SortableTable.defaultColumn('Planned Equipment', 'equipment'));
  }
  const rows = allParameters.map((parameter) => ({
    id: parameter.paramId,
    name: parameter.name,
    equipment: formattedCellValueFromColumnConstraint(parameter.plannedEquipment),
    models: formattedCellValueFromColumnConstraint(parameter.models),
    checked: selectedParameterIds.includes(parameter.paramId),
  }));
  return allParameters.length === 0 ? (
    <p>No equipment in unit operation.</p>
  ) : (
    <SearchableCheckableSortableTable
      rows={rows}
      columns={columns}
      onChangeChecks={props.onChangeSelectedParameterIds}
      objectName="equipment"
      numSelected={selectedParameterIds.length}
    />
  );
}

const styles = StyleSheet.create({
  tableWithCountWrapper: {
    borderRadius: '4px',
    border: `1px solid ${GRAY}`,
    marginTop: '16px',
  },
  selectedCount: {
    background: GRAY_LIGHTER,
    padding: '10px 8px',
    borderBottom: `1px solid ${GRAY}`,
  },
  table: {
    marginBottom: 0,
  },
});
